import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Loader2, Send, Gavel, Flag, Award, PenLine } from 'lucide-react';
import { BriefDialog } from './BriefDialog';
import { MicButton, useSpeaker } from '../voice/VoiceControls';
import { ProceduralBriefing } from './ProceduralBriefing';

const Bubble = ({ m }) => (
  <div className={`flex ${m.role === 'judge' ? 'justify-start' : 'justify-end'}`}>
    <div className={`max-w-[85%] rounded-2xl px-4 py-2 text-sm ${m.role === 'judge' ? 'bg-slate-800 text-slate-100 rounded-bl-md' : 'bg-amber-500 text-slate-950 rounded-br-md'}`}>
      {m.role === 'judge' && <p className="text-[10px] uppercase tracking-wider text-amber-400 mb-1 flex items-center gap-1"><Gavel className="w-3 h-3" />Juez</p>}
      <p className="whitespace-pre-line">{(m.text || '').replace(/\*\*|__|^#+\s*/gm, '')}</p>
    </div>
  </div>
);

const Evaluation = ({ ev }) => (
  <div data-testid="rehearsal-evaluation" className="space-y-3 bg-slate-900 border border-amber-500/40 rounded-xl p-4">
    <div className="flex items-center justify-between">
      <p className="text-sm font-semibold text-amber-300 flex items-center gap-2"><Award className="w-4 h-4" />Evaluación del ensayo</p>
      <p data-testid="rehearsal-score" className="text-3xl font-bold text-amber-400">{Number(ev.nota).toFixed(1)}</p>
    </div>
    <p className="text-sm text-slate-300">{ev.veredicto_probable}</p>
    <div className="grid md:grid-cols-2 gap-3 text-sm">
      <div><p className="text-green-400 font-semibold mb-1">Fortalezas</p><ul className="space-y-1 text-slate-300">{(ev.fortalezas || []).map((t, i) => <li key={i}>• {t}</li>)}</ul></div>
      <div><p className="text-orange-400 font-semibold mb-1">Mejoras</p><ul className="space-y-1 text-slate-300">{(ev.mejoras || []).map((t, i) => <li key={i}>• {t}</li>)}</ul></div>
    </div>
    {(ev.respuestas_modelo || []).map((r, i) => (
      <div key={i} className="text-sm bg-slate-800/60 rounded-lg p-3"><p className="text-slate-200">P: {r.pregunta}</p><p className="text-slate-400">R ideal: {r.respuesta_ideal}</p></div>
    ))}
    {ev.frase_de_cierre && <p className="text-sm italic text-amber-200">«{ev.frase_de_cierre}»</p>}
  </div>
);

export const RehearsalDialog = ({ matter, open, onOpenChange, onUpdated = () => {} }) => {
  const { api } = useAuth();
  const [session, setSession] = useState(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [evaluation, setEvaluation] = useState(null);
  const [briefing, setBriefing] = useState(false);
  const [voiceOn, setVoiceOn] = useState(() => localStorage.getItem('rehearsalVoice') !== 'off');
  const speak = useSpeaker();
  const started = useRef(false);
  const end = useRef(null);

  useEffect(() => {
    if (!open || started.current) return;
    started.current = true;
    setBusy(true);
    api.get(`/crm/matters/${matter.id}/rehearsal/latest`)
      .then(async (r) => {
        const last = r.data.session;
        if (last && (last.status !== 'finished' || last.evaluation)) {
          setSession(last);
          if (last.evaluation) setEvaluation(last.evaluation);
          return;
        }
        const n = await api.post(`/crm/matters/${matter.id}/rehearsal`);
        setSession(n.data);
      })
      .catch((e) => toast.error(e.response?.data?.detail || 'No se pudo iniciar el ensayo'))
      .finally(() => setBusy(false));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { end.current?.scrollIntoView({ behavior: 'smooth' }); }, [session, evaluation]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !session) return;
    const mine = { role: 'lawyer', text: text.trim() };
    setSession({ ...session, messages: [...session.messages, mine] });
    setText('');
    setBusy(true);
    try {
      const r = await api.post(`/crm/rehearsals/${session.id}/message`, { text: mine.text });
      setSession((s) => ({ ...s, messages: [...s.messages, r.data.message], turns_left: r.data.turns_left }));
      if (voiceOn && r.data.message?.text) speak(r.data.message.text, true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'El juez no respondió');
    } finally {
      setBusy(false);
    }
  };

  const finish = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/crm/rehearsals/${session.id}/finish`);
      setEvaluation(r.data.evaluation);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo evaluar');
    } finally {
      setBusy(false);
    }
  };

  const restart = async () => {
    setEvaluation(null);
    setSession(null);
    setBusy(true);
    try {
      const n = await api.post(`/crm/matters/${matter.id}/rehearsal`);
      setSession(n.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo iniciar el ensayo');
    } finally {
      setBusy(false);
    }
  };

  const done = evaluation || session?.status === 'closed' || session?.turns_left === 0;
  const inBriefing = session?.status === 'briefing';
  const onBegin = (data) => {
    setSession((s) => ({ ...s, status: 'active', messages: [data.message] }));
    if (voiceOn) speak(data.message.text, true);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="rehearsal-dialog" className="bg-slate-950 border-slate-700 text-white max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-white">Simulador de Alegato</DialogTitle>
          <DialogDescription className="text-slate-400">
            Ante {matter.assigned_judge_name || 'el juez de la causa'} · {matter.hearing_type || 'Audiencia'} · {matter.title}
          </DialogDescription>
          <label className="flex items-center gap-2 text-xs text-slate-300 pt-1">
            <input data-testid="rehearsal-voice-toggle" type="checkbox" checked={voiceOn} onChange={(e) => { setVoiceOn(e.target.checked); localStorage.setItem('rehearsalVoice', e.target.checked ? 'on' : 'off'); if (!e.target.checked) speak.stop(); }} />
            El juez habla en voz alta (desmarca para solo texto) · usa el micrófono para hablar
          </label>
        </DialogHeader>
        {inBriefing ? (
          <div className="flex-1 overflow-y-auto pr-1 min-h-[240px]">
            <ProceduralBriefing session={session} setSession={setSession} voiceOn={voiceOn} speak={speak} onBegin={onBegin} />
          </div>
        ) : (
        <div data-testid="rehearsal-messages" className="flex-1 overflow-y-auto space-y-3 pr-1 min-h-[240px]">
          {(session?.messages || []).map((m, i) => <Bubble key={i} m={m} />)}
          {busy && <p className="text-xs text-slate-400 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" />{evaluation === null && session?.messages?.length ? 'El juez está pensando...' : 'Abriendo la audiencia...'}</p>}
          {evaluation && <Evaluation ev={evaluation} />}
          {evaluation && (
            <div className="flex flex-wrap items-center gap-3">
              <button data-testid="rehearsal-to-brief-btn" onClick={() => setBriefing(true)} className="text-xs bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-full px-4 py-2 flex items-center gap-1.5"><PenLine className="w-3.5 h-3.5" />Redactar descargos con estas mejoras</button>
              <button data-testid="rehearsal-restart-btn" onClick={restart} className="text-xs text-amber-400 underline">Nuevo ensayo</button>
            </div>
          )}
          {briefing && <BriefDialog matter={matter} open={briefing} onOpenChange={setBriefing} onUpdated={onUpdated} fromRehearsal />}
          <div ref={end} />
        </div>
        )}
        {!evaluation && !inBriefing && (
          <form onSubmit={send} className="flex gap-2 pt-2 border-t border-slate-800">
            <textarea
              data-testid="rehearsal-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) send(e); }}
              placeholder={done ? 'La audiencia terminó. Pide tu evaluación.' : 'Tu alegato o respuesta al juez...'}
              disabled={busy || !session || done}
              rows={2}
              className="flex-1 bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white resize-none"
            />
            <div className="flex flex-col gap-2">
              <MicButton testid="rehearsal-mic-btn" disabled={busy || !session || done} onText={(t) => setText((p) => (p ? `${p} ${t}` : t))} />
              <button data-testid="rehearsal-send-btn" type="submit" disabled={busy || !session || done} className="bg-amber-500 text-slate-950 rounded-lg px-3 py-2 disabled:opacity-50" aria-label="Enviar"><Send className="w-4 h-4" /></button>
              <button data-testid="rehearsal-finish-btn" type="button" onClick={finish} disabled={busy || !session || session.messages.length < 2} className="border border-slate-600 text-slate-300 rounded-lg px-3 py-2 disabled:opacity-40" title="Terminar y evaluar"><Flag className="w-4 h-4" /></button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
